"use client";

import {
  Dropdown,
  DropdownContent,
  DropdownTrigger,
} from "@/components/ui/dropdown";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { BellIcon } from "./icons";

/**
 * Колокольчик с новыми объявлениями по подпискам.
 *
 * Счётчик обновляется при открытии страницы и далее раз в пять минут —
 * чаще не нужно, воркер проверяет подписки каждые двадцать.
 */

interface FeedItem {
  subscriptionId: number;
  subscriptionName: string;
  trdBuyId: number;
  numberAnno: string | null;
  nameRu: string | null;
  totalSum: number | null;
  endDate: string | null;
}

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export function Notification() {
  const [isOpen, setIsOpen] = useState(false);
  const [unseen, setUnseen] = useState(0);
  const [items, setItems] = useState<FeedItem[]>([]);
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    try {
      const response = await fetch("/api/subscriptions/feed?limit=6&onlyUnseen=1");
      if (!response.ok) return;
      const data = await response.json();
      if (data?.success) {
        setUnseen(data.unseen || 0);
        setItems(data.items || []);
      }
    } catch {
      /* молча: колокольчик не должен мешать работе */
    }
  }, []);

  useEffect(() => {
    // Чтение внешнего состояния с сервера, а не синхронизация внутреннего
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
    const timer = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const formatAmount = (value: number | null) =>
    typeof value === "number" ? `${value.toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₸` : "";

  return (
    <Dropdown
      isOpen={isOpen}
      setIsOpen={(open) => {
        setIsOpen(open);
        if (open) load();
      }}
    >
      <DropdownTrigger
        className="grid size-12 place-items-center rounded-full border bg-gray-2 text-dark outline-none hover:text-primary focus-visible:border-primary focus-visible:text-primary dark:border-dark-4 dark:bg-dark-3 dark:text-white dark:focus-visible:border-primary"
        aria-label="Новое по подпискам"
      >
        <span className="relative">
          <BellIcon />
          {unseen > 0 && (
            <span className="absolute right-0 top-0 z-10 -translate-y-1/3 translate-x-1/3 rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-4 text-white">
              {unseen > 99 ? "99+" : unseen}
              <span className="sr-only">новых объявлений по подпискам</span>
            </span>
          )}
        </span>
      </DropdownTrigger>

      <DropdownContent
        align={isMobile ? "end" : "center"}
        className="border border-stroke bg-white px-3.5 py-3 shadow-md dark:border-dark-3 dark:bg-gray-dark min-[350px]:min-w-[20rem]"
      >
        <div className="mb-1 flex items-center justify-between px-2 py-1.5">
          <span className="text-lg font-medium text-dark dark:text-white">Новое по подпискам</span>
          {unseen > 0 && (
            <span className="rounded-md bg-primary px-[9px] py-0.5 text-xs font-medium text-white">
              {unseen}
            </span>
          )}
        </div>

        {items.length === 0 ? (
          <p className="px-2 py-4 text-sm text-dark-5 dark:text-dark-6">
            Новых объявлений нет
          </p>
        ) : (
          <ul className="mb-3 max-h-[23rem] space-y-1.5 overflow-y-auto">
            {items.map((item) => (
              <li key={`${item.subscriptionId}-${item.trdBuyId}`} role="menuitem">
                <Link
                  href={`/pages/tenders/details/${item.trdBuyId}`}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "flex flex-col gap-0.5 rounded-lg px-2 py-2 outline-none hover:bg-gray-2 focus-visible:bg-gray-2 dark:hover:bg-dark-3 dark:focus-visible:bg-dark-3",
                  )}
                >
                  <strong className="block text-sm font-medium text-dark dark:text-white">
                    {item.nameRu || item.numberAnno || `Объявление ${item.trdBuyId}`}
                  </strong>
                  <span className="truncate text-xs font-medium text-dark-5 dark:text-dark-6">
                    {formatAmount(item.totalSum)}
                    {item.totalSum !== null && item.subscriptionName ? " · " : ""}
                    подписка «{item.subscriptionName}»
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <Link
          href="/pages/subscriptions"
          onClick={() => setIsOpen(false)}
          className="block rounded-lg border border-primary p-2 text-center text-sm font-medium tracking-wide text-primary outline-none transition-colors hover:bg-blue-light-5 focus:bg-blue-light-5 focus:text-primary focus-visible:border-primary dark:border-dark-3 dark:text-dark-6 dark:hover:border-dark-5 dark:hover:bg-dark-3 dark:hover:text-dark-7 dark:focus-visible:border-dark-5 dark:focus-visible:bg-dark-3 dark:focus-visible:text-dark-7"
        >
          Все подписки
        </Link>
      </DropdownContent>
    </Dropdown>
  );
}
